import React from 'react';

interface UseAdvisorFormHandlersProps {
  advisorState: AdvisorProfile;
  setAdvisorState: React.Dispatch<React.SetStateAction<AdvisorProfile>>;
  setHasUnsavedChanges: (hasChanges: boolean) => void;
  advisor: AdvisorProfile;
}

export function useAdvisorFormHandlers({
  advisorState,
  setAdvisorState,
  setHasUnsavedChanges,
  advisor,
}: UseAdvisorFormHandlersProps) {
  const handleAdvisorStateChange =
    (advisorKey: keyof AdvisorProfile) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setAdvisorState(prevState => ({
        ...prevState,
        [advisorKey]: event.target.value,
      }));
      setHasUnsavedChanges(true);
    };

  const formatPhoneNumber = (value: string): string => {
    // Remove all non-digit characters
    const digits = value.replace(/\D/g, '');

    // Format as US phone number if 10 digits
    if (digits.length <= 10) {
      if (digits.length <= 3) return digits;
      if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    }

    // For international numbers, just return the digits with a + prefix
    return `+${digits}`;
  };

  const handlePhoneNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(event.target.value);
    setAdvisorState(prevState => ({
      ...prevState,
      advisorBusinessPhoneNumber: formatted,
    }));
    setHasUnsavedChanges(true);
  };

  const handleAddressFieldChange = (field: keyof Address, value: string) => {
    setAdvisorState(prevState => ({
      ...prevState,
      advisorBusinessAddress: prevState.advisorBusinessAddress
        ? {
            ...prevState.advisorBusinessAddress,
            [field]: value,
          }
        : {
            advisorId: advisor.advisorId,
            addressType: 'business' as const,
            streetAddress: field === 'streetAddress' ? value : '',
            apartment: field === 'apartment' ? value : '',
            city: field === 'city' ? value : '',
            state: field === 'state' ? value : '',
            zipCode: field === 'zipCode' ? value : '',
            country: field === 'country' ? value : 'US',
          },
    }));
    setHasUnsavedChanges(true);
  };

  const handleShouldSendClientEmailChange = (value: string | number) => {
    setAdvisorState(prevState => ({
      ...prevState,
      sendClientResultsLinkSetting: value === 'yes',
    }));
    setHasUnsavedChanges(true);
  };

  const handleOverviewSlideChange = (checked: boolean) => {
    setAdvisorState(prevState => ({
      ...prevState,
      defaultClientOnboardingOverviewSlide: checked,
    }));
    setHasUnsavedChanges(true);
  };

  return {
    handleAdvisorStateChange,
    handlePhoneNumberChange,
    handleAddressFieldChange,
    handleShouldSendClientEmailChange,
    handleOverviewSlideChange,
  };
}
